import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./EmployeeDashboard.css";
import "./AdminDashboard.css";

const PURPLE = "#6c63ff";

const sections = [
  {
    group: "Get Started",
    items: [
      {
        number: "1",
        title: "Account Setup & Login",
        subtitle: "Create your account or log in",
        icon: "📱",
        iconBg: "#e8f4fd",
        content: [
          { heading: "Logging In", body: "Enter your 10-digit phone number. The system formats it automatically as (XXX) XXX-XXXX. Press Next to continue. If your number is not recognized, contact your administrator." },
          { heading: "First Time? Create Your Password", body: "If this is your first login, enter a new password and confirm it. You will see a green 'Passwords match ✓' indicator when they match. Press Create Account & Login to activate your account." },
          { heading: "Returning Employees", body: "Enter your phone number, then your existing password, and press Login." },
          { heading: "Location Permission", body: "After logging in, the system will request your GPS location. You must allow this. Location is required to verify you are at the client's home before you can clock in. If you deny location access, clock-in will not work." },
        ],
      },
      {
        number: "2",
        title: "Dashboard Overview",
        subtitle: "Understand your dashboard",
        icon: "⊞",
        iconBg: "#e8f5e9",
        content: [
          { heading: "What You See After Login", body: "Your dashboard shows your first name, today's date, your upcoming schedules, and your current shift status." },
          { heading: "Live Shift Timer", body: "If you are clocked in, a timer displays in HH:MM:SS format and updates every second. It continues running even if you close the app and resumes automatically when you return." },
          { heading: "Upcoming Schedules", body: "All upcoming shifts load automatically. If no shifts appear, you have no scheduled work at this time." },
          { heading: "Automatic Shift Restoration", body: "If you were clocked in and then closed the app, your session is restored automatically when you reopen it. You do not need to do anything — the timer picks up where it left off." },
        ],
      },
      {
        number: "3",
        title: "Location Requirements",
        subtitle: "What you need to know",
        icon: "📍",
        iconBg: "#ede7f6",
        content: [
          { heading: "Why Location Is Required", body: "The system uses your GPS coordinates to verify that you are physically at the client's home before allowing you to clock in." },
          { heading: "Proximity Requirement", body: "You must be within 0.1 km (approximately 328 feet) of the client's address. If you are too far away, you will see: '📍 You are too far from the client location to clock in.'" },
          { heading: "Keep GPS Enabled", body: "Make sure your phone's GPS is turned on and you have allowed location access to this app. If location access was denied, go to your phone's Settings and enable it for this app." },
          { heading: "Location Is Not Required For", body: "Clock-out, viewing your schedule, or using the dashboard do not require your location." },
        ],
      },
    ],
  },
  {
    group: "During Your Shift",
    items: [
      {
        number: "4",
        title: "Clock-In Rules",
        subtitle: "All requirements to clock in",
        icon: "🕐",
        iconBg: "#e0f2f1",
        content: [
          { heading: "You Must Have a Shift Today", body: "The system checks whether you have a scheduled shift for today. If none exists, you will see: 'You do not have a scheduled shift today.' Contact your administrator if you believe this is an error." },
          { heading: "You Must Be On Time", body: "You can clock in no earlier than 30 minutes before your scheduled start time. If you try earlier, you will see: '⏰ You are too early to clock in.'" },
          { heading: "You Must Be at the Client's Home", body: "You must be within 0.1 km of the client's address. See Location Requirements for details." },
          { heading: "You Cannot Clock In Twice", body: "If you are already clocked in for today, the system will block a second clock-in and show: '⚠ You are already clocked in for today.'" },
          { heading: "Successful Clock-In", body: "When all conditions are met, your shift begins, the live timer starts, and a success message appears. Your shift is now active." },
        ],
      },
      {
        number: "5",
        title: "Clock-Out Rules",
        subtitle: "How to clock out correctly",
        icon: "🕐",
        iconBg: "#fff3e0",
        content: [
          { heading: "How to Clock Out", body: "Press the Clock Out button on your dashboard. Location is not required for clock-out." },
          { heading: "Successful Clock-Out", body: "Your shift ends, the timer resets, and a green success message appears. The completed shift disappears from your schedule list." },
          { heading: "Clock-Out Errors", body: "Clock-out may fail if you were never clocked in, already clocked out, or your session expired. A red error message will appear explaining the issue." },
        ],
      },
      {
        number: "6",
        title: "Late Shift Handling",
        subtitle: "What happens if you're late",
        icon: "⚠️",
        iconBg: "#fdecea",
        content: [
          { heading: "Grace Period", body: "You have 30 minutes after your scheduled end time to clock out normally." },
          { heading: "Late Prompt", body: "If you remain clocked in more than 30 minutes past your scheduled end time, a prompt will appear asking if you want to clock out. Tap 'Yes, clock out' to end your shift." },
          { heading: "Why This Matters", body: "This prevents accidental overtime from being recorded. Always clock out at the end of your shift." },
        ],
      },
    ],
  },
  {
    group: "Important Information",
    items: [
      {
        number: "7",
        title: "What the System Stores",
        subtitle: "Your data & how it's used",
        icon: "🗄",
        iconBg: "#e0f7fa",
        content: [
          { heading: "Data Stored on Your Device", body: "Your phone number, employee ID, JWT token, role, clock-in time (when active), and last known GPS coordinates are stored locally to keep you logged in and support clock-in/out." },
          { heading: "How It Is Used", body: "This data is used exclusively for authentication, schedule retrieval, and clock-in/out verification. It is not shared externally." },
        ],
      },
      {
        number: "8",
        title: "Your Responsibilities",
        subtitle: "Employee responsibilities summary",
        icon: "🛡",
        iconBg: "#fce4ec",
        content: [
          { heading: "Before Your Shift", body: "Arrive at the client's home, ensure GPS is enabled, and log in with your phone number and password." },
          { heading: "To Clock In", body: "Be within 0.1 km of the client, be within 30 minutes of your start time, have a scheduled shift today, and have location access enabled." },
          { heading: "During Your Shift", body: "The timer runs automatically. You can close the app — your session is preserved." },
          { heading: "To Clock Out", body: "Press Clock Out. Do this within 30 minutes after your shift ends to avoid the late prompt." },
        ],
      },
      {
        number: null,
        title: "Help & Support",
        subtitle: "Get assistance when you need it",
        icon: "❓",
        iconBg: "#ede7f6",
        content: [
          { heading: "Contact Your Administrator", body: "For any issues with your account, schedule, or clock-in problems, contact your administrator directly." },
          { heading: "Common Issues", body: "• Can't clock in → check GPS is on and you're within 328 ft of the client\n• Wrong shift time → contact your administrator\n• Password forgotten → contact your administrator to reset\n• Timer not showing → refresh the page; your session will restore" },
        ],
      },
    ],
  },
];

export default function EmployeeManual() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const isLoggedIn = !!localStorage.getItem("jwt_token");

  const allItems = sections.flatMap((s) => s.items);
  const filteredSections = searchQuery.trim()
    ? [{ group: "Search Results", items: allItems.filter((item) =>
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.subtitle.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.content.some((c) =>
          c.heading.toLowerCase().includes(searchQuery.toLowerCase()) ||
          c.body.toLowerCase().includes(searchQuery.toLowerCase())
        )
      )}]
    : sections;

  return (
    <>
      {/* Exact same nav as EmployeeDashboard */}
      {isLoggedIn && (
        <>
          <nav className="apple-global-nav">
            <div className="left-nav">
              <img src="/images/logoPlaceholder1.png" alt="Logo" className="apple-logo" />
            </div>
            <div className="center-nav">
              <a href="/employee/dashboard">Dashboard</a>
            </div>
            <div className="account-dropdown-container">
              <span className="account-link">Account ▾</span>
              <div className="account-dropdown">
                <a href="/employee/history">History</a>
                <a href="/logout">Log Out</a>
              </div>
            </div>
            <button
              className={`hamburger-btn ${menuOpen ? "open" : ""}`}
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className="bar"></span>
              <span className="bar"></span>
            </button>
          </nav>

          {menuOpen && (
            <div className="admin-mobile-menu">
              <a href="/employee/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</a>
              <a href="/employee/history" onClick={() => setMenuOpen(false)}>History</a>
              <a href="/logout" onClick={() => setMenuOpen(false)}>Log Out</a>
            </div>
          )}
        </>
      )}

      {/* Page body */}
      <div style={styles.outerPage}>
        <div style={styles.pageWrapper}>

          {activeSection ? (
            <>
              <div style={styles.detailHeader}>
                <button onClick={() => setActiveSection(null)} style={styles.backBtn}>‹ Back</button>
                <h2 style={styles.detailTitle}>{activeSection.title}</h2>
              </div>
              <div style={styles.detailScroll}>
                {activeSection.content.map((block, i) => (
                  <div key={i} style={styles.contentBlock}>
                    <p style={styles.blockHeading}>{block.heading}</p>
                    <p style={styles.blockBody}>{block.body}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div style={styles.topBar}>
                <h1 style={styles.pageTitle}>Employee Manual</h1>
                <p style={styles.pageSubtitle}>Your guide to shifts, clocking in &amp; more</p>
              </div>

              <div style={styles.searchRow}>
                <div style={styles.searchBox}>
                  <span style={styles.searchIcon}>🔍</span>
                  <input
                    type="text"
                    placeholder="Search topics"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={styles.searchInput}
                  />
                  {searchQuery
                    ? <span style={styles.clearBtn} onClick={() => setSearchQuery("")}>✕</span>
                    : <span>🎙</span>
                  }
                </div>
              </div>

              {!searchQuery && (
                <div style={styles.welcomeCard}>
                  <div style={{ ...styles.welcomeIconBox, background: PURPLE }}>
                    <span style={{ fontSize: 28 }}>👤</span>
                  </div>
                  <div>
                    <p style={styles.welcomeTitle}>Welcome to Oura</p>
                    <p style={styles.welcomeBody}>Everything you need to know as an employee — all in one place.</p>
                  </div>
                </div>
              )}

              {filteredSections.map((section) => (
                <div key={section.group}>
                  <p style={styles.groupLabel}>{section.group}</p>
                  <div style={styles.cardGroup}>
                    {section.items.map((item, i) => (
                      <div
                        key={i}
                        style={{ ...styles.rowItem, borderBottom: i < section.items.length - 1 ? "0.5px solid #f0f0f5" : "none" }}
                        onClick={() => setActiveSection(item)}
                      >
                        <div style={{ ...styles.rowIcon, background: item.iconBg }}>
                          <span style={{ fontSize: 20 }}>{item.icon}</span>
                        </div>
                        <div style={styles.rowText}>
                          <span style={styles.rowTitle}>{item.number ? `${item.number}. ` : ""}{item.title}</span>
                          <span style={styles.rowSubtitle}>{item.subtitle}</span>
                        </div>
                        <span style={styles.chevron}>›</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              {!searchQuery && (
                <div style={styles.banner}>
                  <span style={{ fontSize: 24 }}>🛡</span>
                  <div>
                    <p style={styles.bannerTitle}>Stay Compliant. Stay Reliable.</p>
                    <p style={styles.bannerSubtitle}>Thank you for providing excellent care!</p>
                  </div>
                </div>
              )}

              {searchQuery && filteredSections[0]?.items.length === 0 && (
                <p style={{ textAlign: "center", color: "#8e8e93", marginTop: 40 }}>
                  No results for "{searchQuery}"
                </p>
              )}

              <div style={{ height: 48 }} />
            </>
          )}
        </div>
      </div>
    </>
  );
}

const styles = {
  outerPage: {
    minHeight: "100vh",
    background: "#f2f2f7",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  pageWrapper: {
    maxWidth: 620,
    margin: "0 auto",
    padding: "0 20px 48px",
  },
  topBar: { padding: "40px 0 8px" },
  pageTitle: { fontSize: 30, fontWeight: 700, color: "#1d1d1f", margin: 0, lineHeight: 1.1 },
  pageSubtitle: { fontSize: 15, color: "#6e6e73", marginTop: 4, marginBottom: 0 },
  searchRow: { padding: "10px 0 12px" },
  searchBox: { background: "#e4e4eb", borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 },
  searchIcon: { fontSize: 16, color: "#8e8e93" },
  searchInput: { flex: 1, background: "transparent", border: "none", outline: "none", fontSize: 15, color: "#1d1d1f", fontFamily: "inherit" },
  clearBtn: { fontSize: 14, color: "#8e8e93", cursor: "pointer", padding: "0 2px" },
  welcomeCard: { background: "#fff", borderRadius: 16, padding: 16, border: "0.5px solid #e0e0e5", display: "flex", alignItems: "center", gap: 14, marginBottom: 24 },
  welcomeIconBox: { width: 56, height: 56, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  welcomeTitle: { fontSize: 17, fontWeight: 600, color: "#1d1d1f", margin: "0 0 4px" },
  welcomeBody: { fontSize: 14, color: "#6e6e73", lineHeight: 1.4, margin: 0 },
  groupLabel: { fontSize: 18, fontWeight: 700, color: "#1d1d1f", margin: "4px 0 8px" },
  cardGroup: { background: "#fff", borderRadius: 16, overflow: "hidden", border: "0.5px solid #e0e0e5", marginBottom: 24 },
  rowItem: { display: "flex", alignItems: "center", gap: 14, padding: "13px 16px", cursor: "pointer", WebkitTapHighlightColor: "transparent" },
  rowIcon: { width: 38, height: 38, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  rowText: { flex: 1, display: "flex", flexDirection: "column", gap: 2 },
  rowTitle: { fontSize: 15, fontWeight: 500, color: "#1d1d1f" },
  rowSubtitle: { fontSize: 13, color: "#8e8e93" },
  chevron: { fontSize: 20, color: "#c7c7cc", lineHeight: 1 },
  banner: { background: PURPLE, borderRadius: 16, padding: "16px 20px", display: "flex", alignItems: "center", gap: 14, marginBottom: 24 },
  bannerTitle: { color: "#fff", fontSize: 15, fontWeight: 600, margin: "0 0 2px" },
  bannerSubtitle: { color: "rgba(255,255,255,0.8)", fontSize: 13, margin: 0 },
  detailHeader: { padding: "40px 0 16px", display: "flex", alignItems: "center", gap: 12 },
  backBtn: { background: "none", border: "none", fontSize: 17, color: PURPLE, cursor: "pointer", padding: 0, fontFamily: "inherit", fontWeight: 500, flexShrink: 0 },
  detailTitle: { fontSize: 20, fontWeight: 600, color: "#1d1d1f", margin: 0 },
  detailScroll: { display: "flex", flexDirection: "column", gap: 12, paddingBottom: 48 },
  contentBlock: { background: "#fff", borderRadius: 16, padding: "14px 16px", border: "0.5px solid #e0e0e5" },
  blockHeading: { fontSize: 15, fontWeight: 600, color: "#1d1d1f", margin: "0 0 6px" },
  blockBody: { fontSize: 14, color: "#3a3a3c", lineHeight: 1.55, margin: 0, whiteSpace: "pre-line" },
};